import { EMPTY_TIME, TimeLeft } from '~/shared/utils/calculate-time-left';

type TimeLeftUnit = keyof TimeLeft;

const DA_UNIT_LABELS: Record<TimeLeftUnit, { one: string; other: string }> = {
    days: { one: 'dag', other: 'dage' },
    hours: { one: 'time', other: 'timer' },
    minutes: { one: 'minut', other: 'minutter' },
    seconds: { one: 'sekund', other: 'sekunder' },
};

const TIME_LEFT_UNITS: TimeLeftUnit[] = ['days', 'hours', 'minutes', 'seconds'];

export type FormatTimeLeftRemainingOptions = {
    /** Max non-zero units to include, e.g. 2 → "1 dag og 2 timer". */
    maxUnits?: number;
    /** Word between units. Default: " og ". */
    conjunction?: string;
};

const formatUnit = (unit: TimeLeftUnit, value: number): string => {
    const labels = DA_UNIT_LABELS[unit];
    const label = value === 1 ? labels.one : labels.other;
    return `${value} ${label}`;
};

/**
 * Human-readable remaining time in Danish, e.g. "1 time og 30 minutter og 1 sekund".
 * Intended for compact UIs such as sticky PDP CTAs ("Live om …").
 */
export const formatTimeLeftRemaining = (
    timeLeft: TimeLeft,
    { maxUnits, conjunction = ' og ' }: FormatTimeLeftRemainingOptions = {},
): string => {
    const parts = TIME_LEFT_UNITS.reduce<string[]>((acc, unit) => {
        const value = timeLeft[unit];
        if (value > 0) acc.push(formatUnit(unit, value));
        return acc;
    }, []);

    if (!parts.length) {
        return formatUnit('seconds', 0);
    }

    const limitedParts = maxUnits ? parts.slice(0, maxUnits) : parts;
    return limitedParts.join(conjunction);
};

/**
 * Prefix wrapper for sticky-style copy, e.g. "Live om 2 dage og 1 time".
 */
export const formatTimeLeftWithPrefix = (
    timeLeft: TimeLeft,
    prefix: string,
    options?: FormatTimeLeftRemainingOptions,
): string => {
    if (
        timeLeft.days === 0 &&
        timeLeft.hours === 0 &&
        timeLeft.minutes === 0 &&
        timeLeft.seconds === 0
    ) {
        return prefix.trim();
    }

    return `${prefix} ${formatTimeLeftRemaining(timeLeft, options)}`.trim();
};

export { EMPTY_TIME };
